/**
 * The named-cue registry: a pure name → {@link CueSource} map. It holds no
 * audio state, so it is fully unit-testable without a context. The engine owns
 * one of these and, at play time, resolves the name ONCE and plays whatever
 * kind of source it finds:
 *
 *  - `samples` — a pinned bank cue: the engine sample ids of its variants, in
 *    variant order, played round-robin so the bank's bounded variation is
 *    heard without any randomness (a replay sounds the same as the match).
 *  - `recipe`  — a native game's own procedural cue (`registerCue`), a pure
 *    function of `(ctx, when, params)` scheduled against the synth context.
 *
 * Two kinds of source, one lookup: there is no "try the bank, else fall back"
 * — a name resolves to exactly one entry, and replacing a name replaces it.
 */
import { PlatformError } from '@djgg/core';
import type { CueRecipe } from './types.js';

/** What a registered name plays. */
export type CueSource =
  | { readonly kind: 'recipe'; readonly recipe: CueRecipe }
  | { readonly kind: 'samples'; readonly ids: readonly string[]; cursor: number };

/** Immutable, ordered snapshot of the registry. */
export interface CueEntry {
  readonly name: string;
  readonly source: CueSource;
}

/** A pure name → source registry. No Web Audio here. */
export class CueRegistry {
  private readonly cues = new Map<string, CueSource>();

  /**
   * Register (or replace) a procedural recipe under `name`. Replacing an
   * existing name is allowed and intentional — a game may override a platform
   * cue with its own themed variant.
   */
  register(name: string, recipe: CueRecipe): void {
    this.cues.set(this.validName(name), { kind: 'recipe', recipe });
  }

  /** Register (or replace) a sample-backed cue: one or more engine sample ids
   *  played in rotation. An empty id list is a programming error. */
  registerSamples(name: string, ids: readonly string[]): void {
    if (ids.length === 0) {
      throw new PlatformError('validation', `sample cue "${name}" needs at least one sample id`);
    }
    this.cues.set(this.validName(name), { kind: 'samples', ids: [...ids], cursor: 0 });
  }

  /** True if `name` has a registered source. */
  has(name: string): boolean {
    return this.cues.has(name);
  }

  /** The source for `name`, or `undefined` if unregistered. */
  get(name: string): CueSource | undefined {
    return this.cues.get(name);
  }

  /** All registered cue names, in insertion order. */
  names(): string[] {
    return [...this.cues.keys()];
  }

  /** An ordered snapshot of every entry. */
  entries(): CueEntry[] {
    return [...this.cues.entries()].map(([name, source]) => ({ name, source }));
  }

  private validName(name: string): string {
    if (!name) {
      throw new PlatformError('validation', 'cue name must be non-empty');
    }
    return name;
  }
}
